import makeApiCall from "../master/axiosIndex";
import axiosInstance from "../axiosInstance";
import Cookies from "js-cookie";

// Base path: /gateWay/smart-mapper
// (the `/gateWay` prefix is already baked into REACT_APP_API_LOGIN_URL)
const BASE = "/smart-mapper";

export type MappingStatus =
  | "queued"
  | "processing"
  | "suggested"
  | "confirmed"
  | "transformed"
  | "failed";

export interface TemplateField {
  name: string;
  label: string;
  type: "string" | "number" | "yes_no" | "date";
  required: boolean;
  is_master_data: boolean;
  description?: string;
}

export interface SmartMapperTemplate {
  template_id: string;
  template_name: string;
  fields: TemplateField[];
}

export interface MappingRow {
  source: string;
  target: string | null;
  confidence: number;
  is_master_data?: boolean;
  comments?: string;
}

export interface ParseResponse {
  request_id: string;
  client_id: string;
  agent_type: string;
  status: string; // "authenticated"
  intake_endpoints: string;
}

export interface AnalyseResponse {
  job_id: string;
  source_file?: string;
  source_headers?: string[];
  source_row_count?: number;
  template_id?: string;
  suggested_mapping?: MappingRow[];
  unmapped_source?: string[];
  unmapped_target?: string[];
  notes?: string[];
  status: string;
  header_row?: number;
  message?: string;
  poll_url?: string;
}

export interface JobResponse {
  job_id: string;
  client_id: string;
  template_type: string;
  source_file_name: string;
  blob_url?: string;
  source_row_count?: number;
  mapping_status: MappingStatus;
  suggested_mapping?: MappingRow[];
  mapping_notes?: string[];
  confirmed_mapping?: MappingRow[] | null;
  transformed_row_count?: number | null;
  validation_errors?: any | null;
  error_message?: string | null;
  llm_prompt_tokens?: number;
  llm_completion_tokens?: number;
  llm_total_tokens?: number;
  llm_cost_usd?: number;
  llm_model?: string;
  created_at: string;
  updated_at: string;
}

export interface ConfirmReviewResponse {
  job_id: string;
  template_type: string;
  source_file: string;
  source_row_count: number;
  current_mapping: MappingRow[];
  unmapped_source: string[];
  unmapped_target: string[];
  status: string;
}

export interface DownloadResult {
  blob: Blob;
  filename: string;
  totalRows: number;
  validRows: number;
  errorRows: number;
}

const smartMapperApis = {
  // §2 — Auth identity check (warmup). UI uses this as the feature gate.
  parse: (): Promise<ParseResponse> =>
    makeApiCall<ParseResponse>({
      url: `${BASE}/parse`,
      method: "post",
    }),

  // §4 — List intake templates.
  getTemplates: (): Promise<SmartMapperTemplate[]> =>
    makeApiCall<SmartMapperTemplate[]>({
      url: `${BASE}/templates`,
      method: "get",
    }),

  // §5 — Upload & analyse Excel. Returns 200 (fast path) or 202 (slow).
  // When the upload is scoped to a site (villa/flat segment templates),
  // the gateway needs the siteId as a query param so the agent can pull
  // the right buyer_materials slice for master-data resolution.
  analyse: (
    templateId: string,
    file: File,
    extra?: { siteId?: string | number }
  ): Promise<AnalyseResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    return makeApiCall<AnalyseResponse>({
      url: `${BASE}/analyse/${templateId}`,
      method: "post",
      data: formData,
      params:
        extra?.siteId != null && extra.siteId !== ""
          ? { siteId: extra.siteId }
          : undefined,
    });
  },

  // §6 — Poll job status.
  getJob: (jobId: string): Promise<JobResponse> =>
    makeApiCall<JobResponse>({
      url: `${BASE}/jobs/${jobId}`,
      method: "get",
    }),

  // §7 — Get suggested mapping for review.
  getConfirm: (jobId: string): Promise<ConfirmReviewResponse> =>
    makeApiCall<ConfirmReviewResponse>({
      url: `${BASE}/confirm/${jobId}`,
      method: "get",
    }),

  // §8 — Submit user-corrected mapping.
  putConfirm: (
    jobId: string,
    confirmedMapping: MappingRow[]
  ): Promise<any> =>
    makeApiCall<any>({
      url: `${BASE}/confirm/${jobId}`,
      method: "put",
      data: { confirmed_mapping: confirmedMapping },
    }),

  // §9 — Trigger row-by-row transform.
  transform: (jobId: string): Promise<any> =>
    makeApiCall<any>({
      url: `${BASE}/transform`,
      method: "post",
      data: { job_id: jobId },
    }),

  // §10 — Download transformed Excel. Returns blob + filename + row counts
  // parsed from response headers. Uses axiosInstance directly because
  // makeApiCall doesn't handle binary streams; we still mirror its auth
  // behaviour (read access-token cookie, send as Authorization header) so
  // the gateway doesn't reject with "No cookies found".
  download: async (jobId: string): Promise<DownloadResult> => {
    const accessToken = Cookies.get("access-token");
    const response = await axiosInstance({
      url: `${BASE}/jobs/${jobId}/download`,
      method: "get",
      responseType: "blob",
      headers: accessToken ? { Authorization: `${accessToken}` } : {},
    });
    const disposition: string = response.headers?.["content-disposition"] || "";
    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["]?([^";]+)/i);
    const filename = decodeURIComponent(
      (filenameMatch?.[1] || `${jobId}.xlsx`).replace(/["]/g, "")
    );
    const totalRows = Number(response.headers?.["x-total-row-count"] || 0);
    const validRows = Number(response.headers?.["x-valid-row-count"] || 0);
    const errorRows = Number(response.headers?.["x-error-row-count"] || 0);
    return {
      blob: response.data as Blob,
      filename,
      totalRows,
      validRows,
      errorRows,
    };
  },

  // §11 — List recent jobs (optional, for "Recent uploads" UI).
  listDocuments: (
    templateId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<any[]> =>
    makeApiCall<any[]>({
      url: `${BASE}/documents/${templateId}`,
      method: "get",
      params,
    }),
};

export default smartMapperApis;
